import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';

/**
 * A modal that actually takes the remote.
 *
 * Two things go wrong with a dialog on a television, and both of them went
 * wrong here before this file existed.
 *
 * 1. IT OPENS WITHOUT FOCUS.
 *
 *    `<Focusable autoFocus>` is deliberately conservative: it only claims
 *    focus when the page has none, because stealing it on a remount — a route
 *    change, a grid refetch — yanks you out of wherever you already are. That
 *    guard is right everywhere except here. When a dialog opens, the thing
 *    behind it still holds focus, so every autoFocus inside the dialog
 *    declines, and the menu appears on screen while the remote keeps driving
 *    the grid underneath it. A modal is precisely the case where taking focus
 *    is the correct behaviour, so it is done HERE rather than by loosening the
 *    guard for everyone.
 *
 * 2. THE ARROW KEYS WALK BACK OUT OF IT.
 *
 *    Spatial navigation reads its candidates from the whole document, and the
 *    grid behind a dialog is still laid out and still visible — it is a scrim,
 *    not a `display: none`. So one press of DOWN leaves the menu and lands on a
 *    channel card that the user cannot see is focused. Marking the container
 *    `data-nav-scope` confines the candidate set to it; see `candidates()`.
 *
 * Focus goes to the first `[data-nav]` inside, in document order — so the
 * default action is written first — and is handed back to whatever had it when
 * the dialog closes, provided that element is still in the document.
 */
export function Modal({
  onClose, className, children, labelledBy,
}: {
  onClose(): void;
  className?: string;
  children: ReactNode;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Layout effect, not a plain one: this must run before the browser paints,
  // or there is a frame in which the dialog is visible and the focus ring is
  // still behind it. On a 60 Hz television that frame is visible.
  useLayoutEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('[data-nav]')?.focus();

    return () => {
      // A dialog often closes BECAUSE the thing behind it went away (hiding a
      // channel removes its card). Restoring focus to a detached node silently
      // drops focus onto <body>, and the next arrow key teleports to the first
      // card in the grid, so check before restoring.
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  // BACK closes the dialog rather than leaving the screen — the reflex
  // everyone has, and the thing a modal that ignores it gets wrong. MENU too:
  // the key that opened it should not open a second one on top.
  useEffect(() => pushKeyLayer((key: NavKey) => {
    if (key === 'back' || key === 'menu') { onClose(); return true; }
    return false;   // the contents are ordinary focusables; let them have it
  }), [onClose]);

  return (
    <div className="scrim" onClick={onClose}>
      <div
        ref={ref}
        data-nav-scope=""
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={className ? `dialog ${className}` : 'dialog'}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
